import {
  ActivatedMobileUserMinimumData,
  DeactivatedMobileUserMinimumData,
  ExportFindingBody,
  FindingLocationSummaryResponse,
} from '../types';
import { client } from './index';
import { withSettleWatchdog } from './settleWatchdog';

interface FetchDeactivatedFindingUsersInterface {
  page: number;
  total_pages: number;
  total_users: number;
  users: DeactivatedMobileUserMinimumData[];
}

export type TicketsAccessLevel = 'full' | 'trial_active' | 'trial_available' | 'closed';

export interface TicketsAccessStatus {
  access: TicketsAccessLevel;
  remaining_trial_days: number | null;
  // Optional: a backend that predates the store-backed findings list sends no
  // such field, and this app must keep working against it.
  search_backend?: 'store' | 'firestore';
}

async function exportFinding(findingId: string, body: ExportFindingBody) {
  const url = `/findings/${findingId}/generate_pdf`;

  const response = await client.post(url, body);
  const { data } = response;
  return data;
}

async function getAllMobileUsersForFindings() {
  const response = await client.get<ActivatedMobileUserMinimumData[]>(
    '/mobile_users_react/findings/all',
  );
  const { data } = response;
  return data;
}

async function getDeactivatedMobileUsersForFindings({ page = 1 }: { page?: number }) {
  const response = await client.get<FetchDeactivatedFindingUsersInterface>(
    `/mobile_users_react/findings/deactivated?page=${page}`,
  );
  const { data } = response;
  return data;
}

async function activateFindingsDemo() {
  const response = await client.post('/users/activate_demo_flag', {
    key: 'Findings',
  });
  const { data } = response;
  return data;
}

// DS-3563: plan-based Tickets access (replaces the legacy 'Findings' flag
// above for this module -- see FindingsHome/Findings.tsx gating).
const TICKETS_ACCESS_TIMEOUT_MS = 10_000;
const TICKETS_WATCHDOG_MESSAGE = 'Tickets access request never settled';

// The axios timeout above only covers a hung *connection*. Both callers below
// gate UI on settling (the paywall's `checked`, the start-trial button's
// disabled state), so a request that hangs forever is the difference between
// failing open and a permanently stuck screen. See `withSettleWatchdog` for
// why that can happen even after the response has technically arrived.
async function getTicketsAccessStatus() {
  const response = await withSettleWatchdog(
    client.get<TicketsAccessStatus>('/findings/access_status', { timeout: TICKETS_ACCESS_TIMEOUT_MS }),
    TICKETS_WATCHDOG_MESSAGE,
  );
  const { data } = response;
  return data;
}

async function startTicketsTrial() {
  const response = await withSettleWatchdog(
    client.post<TicketsAccessStatus>('/findings/start_trial', undefined, {
      timeout: TICKETS_ACCESS_TIMEOUT_MS,
    }),
    TICKETS_WATCHDOG_MESSAGE,
  );
  const { data } = response;
  return data;
}

// DS-4043/DS-4039. New endpoint for the Tickets block's quick view: a
// permission-scoped count plus up to a handful of recent tickets for a
// location/branch, never the full Firestore collection (that card's
// FETCH_CAP=500 stays server-side). Endpoint name/params follow DS-4039's own
// proposal (`GET /findings/location_summary`), settled here as this card's
// first real consumer -- `location_with_descendants_id` matches the param
// name already used by Firmas/Respuestas/Activos for the same kind of filter.
async function getFindingsLocationSummary(params: {
  locationWithDescendantsId: number[];
  dateFrom?: string;
  dateTo?: string;
  /** FindingsController::MAX_LIMIT is 50; the endpoint defaults to 3 (the
   *  quick view's own count) when omitted. Pass a higher value to have
   *  something real to search over client-side (no `search` param on this
   *  endpoint -- see TicketsBlock.tsx). */
  limit?: number;
}) {
  const response = await client.get<FindingLocationSummaryResponse>('/findings/location_summary', {
    params: {
      // Comma-joined scalar, matching the shape DS-4046/c05a55df unified across
      // Firmas/Respuestas/Activos for this same filter -- see `formAnswers.ts`'s
      // `location_with_descendants_id`. An empty array must be omitted
      // entirely (axios drops an `undefined` param), not sent as the literal
      // empty string `formAnswers.ts`'s own length guard already avoids.
      location_with_descendants_id:
        params.locationWithDescendantsId.length > 0
          ? params.locationWithDescendantsId.join(',')
          : undefined,
      date_from: params.dateFrom,
      date_to: params.dateTo,
      limit: params.limit,
    },
  });
  const { data } = response;
  return data;
}

export const findingsApi = {
  exportFinding,
  getAllMobileUsersForFindings,
  getDeactivatedMobileUsersForFindings,
  activateFindingsDemo,
  getTicketsAccessStatus,
  startTicketsTrial,
  getFindingsLocationSummary,
};
